import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../common_modal/Modal";
import FLOAT_MENU from "../common_trivial/FLOAT_MENU";
import { upload_image_table } from "../../App/actions/layerActions";
import { set_value_properties } from "../../App/actions/propertiesActions";
import dict from "../../Data/dict.json";
import generate_short_uuid from "../../App/validation/generate_short_uuid";
import icon_upload from "../../Assets/svg/icon_upload.svg";

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ALLOWED_TYPES = ["png", "jpg", "jpeg", "gif", "webp", "svg", "svg+xml"];

export default function C_U_IMG({
  editorState,
  closeEditor,
  onSave,
  row: popupRow,
  field: popupField,
  inPopup = false,
  language = "id",
}) {
  const [modalError, setModalError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const dispatch = useDispatch();
  const fileInput = useRef(null);

  const layer_state = useSelector((state) => state.layer);
  const properties_state = useSelector((state) => state.properties);
  const feature_key_nested_table = properties_state?.feature_key_nested_table;
  const layer_id = layer_state?.layer_id;

  const activeRow = inPopup ? popupRow : editorState?.row;
  const activeField = inPopup ? popupField : editorState?.field;
  const currentValue = activeRow?.properties?.[activeField?.key];

  const displayValue =
    typeof currentValue === "string" && currentValue
      ? decodeURIComponent(currentValue.split("/").pop().split("?")[0])
      : null;

  const processFile = (file) => {
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setModalError(
        dict?.["File size is too big. Maximum 100MB."]?.[language] ||
          "File size is too big. Maximum 100MB allowed.",
      );
      if (fileInput.current) fileInput.current.value = "";
      return;
    }

    const fileExt = file.name.split(".").pop().toLowerCase();
    const fileMimeSubtype = file.type.split("/").pop().toLowerCase();

    if (
      !ALLOWED_TYPES.includes(fileExt) &&
      !ALLOWED_TYPES.includes(fileMimeSubtype)
    ) {
      setModalError(
        dict?.["Invalid file type"]?.[language] ||
          "Invalid image type. Please upload a supported image format.",
      );
      if (fileInput.current) fileInput.current.value = "";
      return;
    }

    const body_file = {
      file: file,
      file_name: `img_%${file?.name}_%${generate_short_uuid()}`,
      dirname: "properties",
    };

    let body_database;
    let is_nested = !!activeRow?.child_uuid;

    if (is_nested) {
      body_database = {
        geo_layer_id: layer_id,
        feature_key: feature_key_nested_table,
        child_uuid: activeRow.child_uuid,
        properties_key: activeField?.key,
        properties_value: "",
      };
    } else {
      body_database = {
        geo_layer_id: layer_id,
        feature_key: activeRow?.key,
        properties_key: activeField?.key,
        properties_value: "",
      };
    }

    const body = {
      body_file,
      body_database,
      mode: "image",
      is_nested,
    };

    dispatch(upload_image_table(body));

    if (!inPopup && closeEditor) {
      closeEditor();
    }
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSave && activeRow && activeField) {
      onSave(activeRow.id, activeField.key, "", currentValue);
    }
    if (!inPopup && closeEditor) {
      closeEditor();
    }
  };

  // Trigger the parent component's C_E_IMG by setting global_image_editor
  const handleViewClick = (e) => {
    if (inPopup) {
      e.stopPropagation();
    } else {
      e.preventDefault();
      e.stopPropagation();
    }

    if (currentValue) {
      const geo_layer = layer_state?.geo_layer_list?.find(
        (item) => item?.geo_layer?._id === layer_state?.layer_id,
      )?.geo_layer;

      const fields_image =
        geo_layer?.fields?.filter((f) => f.type === "image") || [];

      dispatch(
        set_value_properties({
          key: "global_image_editor",
          value: {
            row: activeRow,
            field: activeField,
            fields_image,
          },
        }),
      );
      // Close this floating menu so the user focuses on the popup viewer
      if (!inPopup && closeEditor) {
        closeEditor();
      }
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleInputChange = (e) => {
    processFile(e.target.files[0]);
  };

  const handleClose = () => {
    if (!inPopup && closeEditor) {
      closeEditor();
    }
  };

  const renderContent = () => (
    <div
      style={{
        padding: inPopup ? "5px 0" : "15px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        width: inPopup ? "100%" : "250px",
      }}
    >
      {!inPopup && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <strong style={{ fontSize: "14px", color: "#333" }}>
            {dict?.["Upload Image"]?.[language] || "Upload Image"}
          </strong>
          <button
            className="badge_small cursor_pointer background_grey_light"
            style={{ border: "none", borderRadius: "4px", outline: "none" }}
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      )}

      {currentValue && (
        <div style={{ marginBottom: "2px" }}>
          <span
            style={{
              fontSize: "12px",
              color: "#666",
              display: "block",
              marginBottom: "6px",
            }}
          >
            {dict?.["Current file"]?.[language] || "Current image"}:
          </span>
          <div
            style={{
              padding: "8px",
              backgroundColor: "#f4f6f8",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {/* Small Thumbnail Preview inside the upload modal */}
            <img
              src={currentValue}
              alt="preview"
              style={{
                width: "100%",
                height: "80px",
                objectFit: "cover",
                borderRadius: "4px",
              }}
            />

            <span
              style={{
                fontSize: "10px",
                color: "#475569",
                wordBreak: "break-all",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={displayValue}
            >
              {displayValue}
            </span>

            <div
              style={{
                display: "flex",
                gap: "4px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleViewClick}
                style={{
                  padding: "4px 8px",
                  fontSize: "10px",
                  fontWeight: "bold",
                  color: "#fff",
                  backgroundColor: "#333",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {dict?.["View"]?.[language] || "View"}
              </button>
              <button
                onClick={handleDelete}
                style={{
                  padding: "4px 8px",
                  fontSize: "10px",
                  fontWeight: "bold",
                  color: "#fff",
                  backgroundColor: "#dc2626",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {dict?.["Delete"]?.[language] || "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <label
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          cursor: "pointer",
          padding: "24px 16px",
          border: `2px dashed ${isDragging ? "#6666ff" : "#9ca8bb"}`,
          borderRadius: "8px",
          backgroundColor: isDragging ? "rgba(102, 102, 255, 0.05)" : "#fcfdfe",
          transition: "all 0.2s ease",
          textAlign: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "36px",
            height: "36px",
            backgroundColor: isDragging ? "#6666ff" : "#e6eef3",
            border: `1px solid ${isDragging ? "#6666ff" : "#9ca8bb"}`,
            borderRadius: "50%",
            boxSizing: "border-box",
            transition: "background-color 0.2s ease",
          }}
        >
          <img
            src={icon_upload}
            style={{
              width: "16px",
              height: "16px",
              filter: isDragging ? "brightness(0) invert(1)" : "none",
            }}
            alt="upload system"
          />
        </div>

        <span
          style={{
            fontSize: "12px",
            color: isDragging ? "#6666ff" : "#555",
            fontWeight: "500",
            lineHeight: "1.4",
          }}
        >
          {dict?.["Click or drag file here"]?.[language] ||
            "Click or drag image here"}
        </span>

        <input
          id={`${activeRow?.key}-${activeField?.key}`}
          style={{ display: "none" }}
          type="file"
          ref={fileInput}
          onChange={handleInputChange}
          accept="image/*,.png,.jpg,.jpeg,.gif,.webp,.svg"
        />
      </label>
    </div>
  );

  const renderErrorModal = () =>
    modalError && (
      <Modal
        modalSize="small"
        id="modal-error-img"
        isOpen={!!modalError}
        onClose={() => setModalError("")}
      >
        <div
          className="box-body"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "30px",
            gap: "20px",
          }}
        >
          <span
            style={{
              color: "#d9534f",
              fontSize: "16px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {modalError}
          </span>
          <button
            onClick={() => setModalError("")}
            className="button background_black"
            style={{
              padding: "8px 16px",
              backgroundColor: "#333",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {dict?.["Close"]?.[language] || "Close"}
          </button>
        </div>
      </Modal>
    );

  if (inPopup) {
    const mode_map = properties_state.mode_map;
    return (
      <main>
        {mode_map === "editor" ? (
          <section>
            {renderContent()}
            {renderErrorModal()}
          </section>
        ) : (
          <div className="container background_white text_medium">
            {displayValue || "-"}
            {currentValue && (
              <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                <button
                  onClick={handleViewClick}
                  className="button_small background_black"
                >
                  {dict?.["View"]?.[language] || "View"}
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    );
  }

  return (
    <>
      <FLOAT_MENU
        id="global-cell-editor-img"
        open={true}
        anchorEl={editorState?.anchorEl}
        onClose={handleClose}
        zIndex={3}
      >
        {renderContent()}
      </FLOAT_MENU>
      {renderErrorModal()}
    </>
  );
}
